import React, { useState } from 'react'
import { FloatingInspectorButton, FloatingInspectorButtonProps } from '../FloatingInspectorButton'
import FloatingInspectorButtonIcon from './FloatingInspectorButtonIcon'
import FloatingInspectorButtonText from './FloatingInspectorButtonText'
import MinusIcon from '../FloatingIcons/MinusIcon'
import PlusIcon from '../FloatingIcons/PlusIcon'
import WrapPopover, { PopoverGapType } from '../SharedComponents/Popover/WrapPopover'
import { PopoverWidthType, PopoverPaddingType, PopoverBorderRadius } from '../SharedComponents/Popover/WrapPopover'
import FloatingInspectorButtonInput from './FloatingInspectorButtonInput'
import { lightTheme } from '@/customTheme'

export interface FloatingInspectorButtonPlusMinusProps
  extends Pick<
    FloatingInspectorButtonProps,
    | 'selected'
    | 'disabled'
    | 'style'
    | 'tooltipText'
    | 'placementTooltip'
    | 'marginTooltip'
  > {
  initialValue?: number
  onValueChange?: (value: number, isUnmount?: boolean) => void
  step?: number
  min?: number
  max?: number
  minusTooltipText?: string
  plusTooltipText?: string
  valueTooltipText?: string
  formatValue?: (value: number) => string
  valuesList?: number[]
  popoverWidth?: PopoverWidthType
  popoverPadding?: PopoverPaddingType
  popoverBorderRadius?: PopoverBorderRadius
  popopverGapContent?: PopoverGapType
}

const FloatingInspectorButtonPlusMinus: React.FC<FloatingInspectorButtonPlusMinusProps> = ({
  initialValue = 0,
  onValueChange,
  step = 1,
  min = 6,
  max = 800,
  selected,
  disabled,
  style,
  minusTooltipText = "Decrease value",
  plusTooltipText = "Increase value", 
  valueTooltipText = "Value",
  formatValue,
  valuesList,
  popoverWidth = 70,
  popoverPadding = '8,4',
  popoverBorderRadius = 12,
  popopverGapContent = 16
}) => {
  const [value, setValue] = useState(initialValue)
  // Track if user has modified the value - don't submit unchanged values on unmount
  const hasUserModified = React.useRef(false)
  // Keep local state in sync with external changes (e.g., scaling updates fontSize)
  React.useEffect(() => {
    setValue(initialValue)
    // Reset modification flag when external value changes
    hasUserModified.current = false
  }, [initialValue])
  
  const clamp = (n: number, isUnmount: boolean = false) => {
    if (isUnmount) return n
    return Math.min(Math.max(n, min), max)
  }

  function handleValueChange(newValue: number, isUnmount = false) {
    const next = clamp(newValue, isUnmount)
    if (next === value) return
    // Don't submit rounded values on unmount if user hasn't modified anything
    if (isUnmount && !hasUserModified.current) return
    setValue(next)
    hasUserModified.current = true
    onValueChange?.(next, isUnmount)
  }

  function changeBy(delta: number) {
    setValue(prev => {
      const next = clamp(prev + delta)
      if (next === prev) return prev
      hasUserModified.current = true
      onValueChange?.(next)
      return next
    })
  }

  function onMinusBtnClick() {
    changeBy(-step)
  }

  function onPlusBtnClick() {
    changeBy(step)
  }

  function onInputValueChange(newValue) {
    setValue(newValue)
    hasUserModified.current = true // Mark as modified when user types
  }

  const displayValue = formatValue ? formatValue(value) : String(value)
  const isAtMin = value <= min
  const isAtMax = value >= max

  const popoverContent = valuesList ? ((buttonRef, closePopover) =>
    <WrapPopover width={popoverWidth} padding={popoverPadding} borderRadius={popoverBorderRadius} gap={popopverGapContent} closeOnSelection={true} closePopover={closePopover} buttonRef={buttonRef}>
      <ValuesList valuesList={valuesList} setValue={handleValueChange} />
    </WrapPopover>
  ) : undefined

  return (
    <FloatingInspectorButton
      onClick={() => {}}
      selected={selected}
      disabled={disabled}
      showBorder
      isGroupButton={true}
      style={{ gap: '4px', ...style }}
    >
      <FloatingInspectorButtonIcon
        icon={<MinusIcon fill={isAtMin ? lightTheme.colors.grayScale200 : ''}/>}
        onClick={onMinusBtnClick}
        onLongPressInterval={() => changeBy(-step)}
        disabled={disabled || isAtMin}
        aria-label="Decrement"
        isSecondaryStyle
        tooltipText={minusTooltipText}
      />

      <FloatingInspectorButtonInput
        value={displayValue}
        onChange={onInputValueChange}
        onSubmit={handleValueChange}
        inputType='number'
        onClick={() => {}}
        isSecondaryStyle
        tooltipText={valueTooltipText}
        popoverContent={popoverContent}
      />

      <FloatingInspectorButtonIcon
        icon={<PlusIcon fill={isAtMax ? lightTheme.colors.grayScale200 : ''}/>}
        onClick={onPlusBtnClick}
        onLongPressInterval={() => changeBy(step)}
        disabled={disabled || isAtMax}
        aria-label="Increment"
        isSecondaryStyle
        tooltipText={plusTooltipText}
      />
    </FloatingInspectorButton>
  )
}

export default FloatingInspectorButtonPlusMinus

function ValuesList({ valuesList, setValue }: { valuesList: number[], setValue: (value: number) => void }) {
  return (
    <>
      {valuesList.map((value, index) => (
        <FloatingInspectorButtonText
          key={index}
          onClick={() => setValue(value)}
          text={String(value)}
          style={{ justifyContent: 'center' }}
        />
      ))}
    </>
  )
}
